import React, { useState } from 'react';
import { BellIcon, XIcon, CalendarIcon, ClipboardCheckIcon, AlertCircleIcon, InfoIcon, CheckCircleIcon } from 'lucide-react';
export function NotificationCenter({
  notifications = [],
  onClose,
  onMarkAsRead,
  onMarkAllAsRead
}) {
  const [filter, setFilter] = useState('all');
  // Filter notifications based on the selected filter
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.read;
    return notification.type === filter;
  });
  // Group notifications by date
  const groupedNotifications = filteredNotifications.reduce((groups, notification) => {
    const date = new Date(notification.timestamp).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(notification);
    return groups;
  }, {});
  // Get notification icon based on type
  const getNotificationIcon = type => {
    switch (type) {
      case 'event':
        return <CalendarIcon className="w-5 h-5 text-blue-500" data-id="element-328" />;
      case 'registration':
        return <ClipboardCheckIcon className="w-5 h-5 text-red-500" data-id="element-329" />;
      case 'reminder':
        return <AlertCircleIcon className="w-5 h-5 text-amber-500" data-id="element-330" />;
      case 'system':
        return <InfoIcon className="w-5 h-5 text-gray-500" data-id="element-331" />;
      default:
        return <BellIcon className="w-5 h-5 text-gray-500" data-id="element-332" />;
    }
  };
  return <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-start pt-16 px-4" data-id="element-333">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col" data-id="element-334">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10" data-id="element-335">
          <h2 className="text-lg font-medium" data-id="element-336">Notifications</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-337">
            <XIcon className="w-5 h-5" data-id="element-338" />
          </button>
        </div>
        {/* Filter tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto" data-id="element-339">
          <button className={`px-4 py-2 text-sm font-medium ${filter === 'all' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`} onClick={() => setFilter('all')} data-id="element-340">
            All
          </button>
          <button className={`px-4 py-2 text-sm font-medium ${filter === 'unread' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`} onClick={() => setFilter('unread')} data-id="element-341">
            Unread
          </button>
          <button className={`px-4 py-2 text-sm font-medium ${filter === 'event' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`} onClick={() => setFilter('event')} data-id="element-342">
            Events
          </button>
          <button className={`px-4 py-2 text-sm font-medium ${filter === 'registration' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`} onClick={() => setFilter('registration')} data-id="element-343">
            Registration
          </button>
        </div>
        {filteredNotifications.length === 0 ? <div className="flex-1 flex flex-col items-center justify-center p-8 text-center" data-id="element-344">
            <BellIcon className="w-12 h-12 text-gray-300 mb-3" data-id="element-345" />
            <p className="text-gray-500 font-medium" data-id="element-346">No notifications</p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-347">
              {filter === 'all' ? "You don't have any notifications yet" : `You don't have any ${filter === 'unread' ? 'unread' : filter} notifications`}
            </p>
          </div> : <>
            <div className="flex-1 overflow-y-auto" data-id="element-348">
              {Object.entries(groupedNotifications).map(([date, dateNotifications]) => <div key={date} data-id="element-349">
                    <div className="sticky top-0 bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-350">
                      <h3 className="text-xs font-medium text-gray-500" data-id="element-351">
                        {date}
                      </h3>
                    </div>
                    <div className="divide-y divide-gray-100" data-id="element-352">
                      {dateNotifications.map(notification => <div key={notification.id} className={`p-4 ${!notification.read ? 'bg-blue-50' : ''}`} data-id="element-353">
                          <div className="flex" data-id="element-354">
                            <div className="flex-shrink-0 mr-3" data-id="element-355">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1" data-id="element-356">
                              <div className="flex justify-between" data-id="element-357">
                                <p className={`text-sm font-medium ${!notification.read ? 'text-blue-800' : 'text-gray-800'}`} data-id="element-358">
                                  {notification.title}
                                </p>
                                <span className="text-xs text-gray-500" data-id="element-359">
                                  {new Date(notification.timestamp).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1" data-id="element-360">
                                {notification.message}
                              </p>
                              {notification.action && <button className="mt-2 text-xs font-medium text-blue-600 hover:text-blue-800" data-id="element-361">
                                  {notification.action}
                                </button>}
                            </div>
                            {!notification.read && <button className="ml-2 text-gray-400 hover:text-gray-600" onClick={() => onMarkAsRead(notification.id)} title="Mark as read" data-id="element-362">
                                <CheckCircleIcon className="w-5 h-5" data-id="element-363" />
                              </button>}
                          </div>
                        </div>)}
                    </div>
                  </div>)}
            </div>
            <div className="p-3 bg-gray-50 border-t border-gray-200" data-id="element-364">
              <button className="w-full py-2 px-4 text-sm font-medium text-blue-600 hover:text-blue-800" onClick={onMarkAllAsRead} data-id="element-365">
                Mark all as read
              </button>
            </div>
          </>}
      </div>
    </div>;
}